/**
 * Content Index 유틸리티 함수
 */

import { sha256 } from '$lib/utils/crypto'

/**
 * 콘텐츠 해시 계산
 * crypto utility with Safari fallback
 */
export async function computeContentHash(content: string): Promise<string> {
  return sha256(content)
}

/**
 * 동기적 해시 계산 (간단한 해시 함수)
 * 실시간 처리가 필요한 경우 사용
 */
export function computeContentHashSync(content: string): string {
  let hash = 0
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36)
}

/**
 * 파일 경로 정규화
 */
export function normalizePath(path: string): string {
  // 백슬래시를 슬래시로 통일
  let normalized = path.replace(/\\/g, '/')
  
  // 중복 슬래시 제거
  normalized = normalized.replace(/\/+/g, '/')
  
  // 앞뒤 슬래시 제거
  normalized = normalized.replace(/^\/+|\/+$/g, '')
  
  return normalized
}

/**
 * 파일 확장자 확인
 */
export function hasMarkdownExtension(path: string): boolean {
  const extensions = ['.md', '.markdown', '.mdx', '.mkd', '.mdwn']
  const lowerPath = path.toLowerCase()
  return extensions.some(ext => lowerPath.endsWith(ext))
}

/**
 * 경로가 패턴과 일치하는지 확인
 */
export function matchesPattern(path: string, patterns: string[]): boolean {
  for (const pattern of patterns) {
    if (minimatch(path, pattern)) {
      return true
    }
  }
  return false
}

/**
 * 간단한 glob 패턴 매칭 (minimatch 대체)
 */
function minimatch(path: string, pattern: string): boolean {
  // 간단한 glob 패턴 지원
  const regexPattern = pattern
    .replace(/\./g, '\\.')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.')
    .replace(/\[([^\]]+)\]/g, '[$1]')
  
  const regex = new RegExp(`^${regexPattern}$`)
  return regex.test(path)
}

/**
 * 바이트 크기를 읽기 쉬운 형태로 변환
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * 시간 차이를 읽기 쉬운 형태로 변환
 */
export function formatTimeDiff(ms: number): string {
  const seconds = Math.floor(ms / 1000)
  
  if (seconds < 60) {
    return `${seconds}초`
  }
  
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes}분`
  }
  
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `${hours}시간`
  }
  
  const days = Math.floor(hours / 24)
  return `${days}일`
}

/**
 * 디바운스 함수
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null
  
  return function (...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout)
    }
    
    timeout = setTimeout(() => {
      func(...args)
    }, wait)
  }
}

/**
 * 배치 처리를 위한 청크 분할
 */
export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = []
  
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  
  return chunks
}

/**
 * 병렬 처리 제한
 */
export async function parallelLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = []
  const executing: Set<Promise<void>> = new Set()
  
  for (const item of items) {
    const promise = Promise.resolve()
      .then(() => fn(item))
      .then(result => {
        results.push(result)
      })
      .finally(() => {
        executing.delete(promise)
      })
    
    executing.add(promise)
    
    // 실행 중인 프로미스가 제한에 도달하면 하나가 완료될 때까지 대기
    if (executing.size >= limit) {
      await Promise.race(executing)
    }
  }
  
  // 남은 모든 프로미스 완료 대기
  await Promise.all(executing)
  return results
}

/**
 * 재시도 로직
 */
export async function retry<T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T> {
  try {
    return await fn()
  } catch (error) {
    if (retries === 0) {
      throw error
    }
    
    await new Promise(resolve => setTimeout(resolve, delay))
    return retry(fn, retries - 1, delay * 2)
  }
}

/**
 * 성능 측정
 */
export class PerformanceTracker {
  private marks: Map<string, number> = new Map()
  private measures: Map<string, number[]> = new Map()
  
  mark(name: string): void {
    this.marks.set(name, performance.now())
  }
  
  measure(name: string, startMark: string, endMark?: string): number {
    const start = this.marks.get(startMark)
    if (!start) {
      throw new Error(`Mark ${startMark} not found`)
    }
    
    const end = endMark ? this.marks.get(endMark) : performance.now()
    if (!end) {
      throw new Error(`Mark ${endMark} not found`)
    }
    
    const duration = end - start
    
    if (!this.measures.has(name)) {
      this.measures.set(name, [])
    }
    this.measures.get(name)!.push(duration)
    
    return duration
  }
  
  getAverage(name: string): number {
    const measures = this.measures.get(name)
    if (!measures || measures.length === 0) {
      return 0
    }
    
    const sum = measures.reduce((acc, val) => acc + val, 0)
    return sum / measures.length
  }
  
  getSummary(): Record<string, { count: number; average: number; total: number }> {
    const summary: Record<string, { count: number; average: number; total: number }> = {}
    
    for (const [name, measures] of this.measures) {
      const total = measures.reduce((acc, val) => acc + val, 0)
      summary[name] = {
        count: measures.length,
        average: total / measures.length,
        total
      }
    }
    
    return summary
  }
  
  clear(): void {
    this.marks.clear()
    this.measures.clear()
  }
}

/**
 * 메모리 사용량 체크
 */
export function getMemoryUsage(): { used: number; total: number; percentage: number } {
  if (typeof window !== 'undefined' && (window as any).performance?.memory) {
    const memory = (window as any).performance.memory
    return {
      used: memory.usedJSHeapSize,
      total: memory.jsHeapSizeLimit,
      percentage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100
    }
  }
  
  // Node.js 환경
  if (typeof process !== 'undefined' && process.memoryUsage) {
    const usage = process.memoryUsage()
    return {
      used: usage.heapUsed,
      total: usage.heapTotal,
      percentage: (usage.heapUsed / usage.heapTotal) * 100
    }
  }
  
  return { used: 0, total: 0, percentage: 0 }
}